import { useMemo } from "react";

import type { UseQueryResult } from "@tanstack/react-query";
import { useQueries } from "@tanstack/react-query";

import { MAX_IMAGES_REQUEST_LENGTH } from "app/pages/activation/constants/limitations";
import { splitArrayIntoChunks } from "app/pages/activation/helpers/splitArrayIntoChunks";
import { fetchWebsitesFaviconsQuery } from "app/services/sitesService/queries/sitesQueries";
import type { IWebsitesFavicons } from "app/services/sitesService/types";

const ICON_QUERY_KEY = "icons";

type QueryResult = {
    isError: boolean;
    isFetching: boolean;
    isLoading: boolean;
    data: IWebsitesFavicons;
};

const combine = (results: UseQueryResult<IWebsitesFavicons>[]) =>
    results.reduce<QueryResult>(
        (acc, result) => ({
            data: {
                ...acc.data,
                ...(result.data ?? {}),
            },
            isError: acc.isError || result.isError,
            isFetching: acc.isFetching || result.isFetching,
            isLoading: acc.isLoading || result.isLoading,
        }),
        {
            data: {},
            isError: false,
            isFetching: false,
            isLoading: false,
        },
    );

export const useIconsQuery = (domains: string[], enabled?: boolean) => {
    const domainsChunks = useMemo(() => splitArrayIntoChunks(domains, MAX_IMAGES_REQUEST_LENGTH), [domains]);

    return useQueries({
        queries: domainsChunks.map((chunk) => ({
            queryKey: [ICON_QUERY_KEY, ...chunk],
            queryFn: async () => fetchWebsitesFaviconsQuery(chunk),
            staleTime: Infinity,
            enabled: (enabled ?? true) && domains.length > 0,
        })),
        combine,
    });
};
